/**
 * @perfectserve/env-ps
 *
 * Unified environment configuration client for PerfectServe Web
 * Works seamlessly in both Vite (build-time) and Docker (runtime) environments
 *
 * Inspired by @telmediq/env from Hypernik
 */
/**
 * Get environment configuration from multiple sources
 * Priority order:
 * 1. Runtime config (Docker) - window.PSCONFIG
 * 2. Build-time config (Vite) - import.meta.env
 * 3. Process env (Node.js) - process.env
 */
const getEnvSource = () => {
    const sources = [];
    // Source 1: Process environment (Node.js)
    if (typeof process !== "undefined" && process.env) {
        sources.push(process.env);
    }
    // Source 2: Vite build-time environment
    try {
        // @ts-ignore - import.meta.env is only available in Vite
        if (typeof import.meta !== "undefined" && import.meta.env) {
            // @ts-ignore
            sources.push(import.meta.env);
        }
    }
    catch (e) {
        // Not in Vite environment
    }
    // Source 3: Runtime config from window (Docker - highest priority)
    // Only use window.PSCONFIG if it has actual config values (not just empty object)
    if (typeof window !== "undefined" && window.PSCONFIG && Object.keys(window.PSCONFIG).length > 0) {
        sources.push(window.PSCONFIG);
    }
    // Merge all sources (later sources override earlier ones)
    return Object.assign({}, ...sources);
};
// Create the env object
const rawEnv = getEnvSource();
/**
 * Derive provisioning configuration from environment
 * Falls back to detecting QA vs Production from cspApi URL
 */
const getProvisioningConfig = () => {
    const cspApi = rawEnv.cspApi || rawEnv.CSP_API_URL || rawEnv.VITE_CSP_API_URL || "";
    const isQA = cspApi.includes("qa-ps.com") || cspApi.includes("atl.qa");
    // Default values based on environment
    const defaults = {
        provisioningApi: isQA
            ? "https://userprovisioning.atl.qa-ps.com"
            : "https://userprovisioning.perfectserve.com",
        provisioningSourceId: isQA
            ? "d900c9b8-8b61-4ca9-b91f-ac11b1193ea7"
            : "474c0dcc-bdd5-4557-8406-bc6978546bca",
    };
    return {
        provisioningApi: rawEnv.provisioningApi || rawEnv.PROVISIONING_API || rawEnv.VITE_PROVISIONING_API || defaults.provisioningApi,
        provisioningSourceId: rawEnv.provisioningSourceId ||
            rawEnv.PROVISIONING_SOURCE_ID ||
            rawEnv.VITE_PROVISIONING_SOURCE_ID ||
            defaults.provisioningSourceId,
    };
};
const provisioningConfig = getProvisioningConfig();
/**
 * Unified environment configuration object
 * Access config values like: env.api.system or env.oidc.authority
 */
const env = {
    // Core properties
    // Try: flat properties > VITE_ prefix > clean name > ENV
    environment: rawEnv.environment || rawEnv.VITE_ENV || rawEnv.ENV,
    debug: typeof rawEnv.debug === "boolean" ? rawEnv.debug : rawEnv.DEBUG_MODE === "true" || rawEnv.DEBUG_MODE === true,
    appName: rawEnv.appName || rawEnv.VITE_APP_NAME || rawEnv.APP_NAME || "PerfectServe Web",
    // API Endpoints (flat structure)
    // Priority: camelCase (Docker) > UPPERCASE (Docker) > VITE_ prefix (Vite)
    systemApi: rawEnv.systemApi || rawEnv.SYSTEM_API || rawEnv.VITE_SYSTEM_API,
    attachmentApi: rawEnv.attachmentApi || rawEnv.ATTACHMENT_API || rawEnv.VITE_ATTACHMENT_API,
    directoryApi: rawEnv.directoryApi || rawEnv.DIRECTORY_API || rawEnv.VITE_DIRECTORY_API,
    directorySearchApi: rawEnv.directorySearchApi || rawEnv.DIRECTORY_SEARCH_API || rawEnv.VITE_DIRECTORY_SEARCH_API,
    gatewayApi: rawEnv.gatewayApi || rawEnv.GATEWAY_API || rawEnv.VITE_GATEWAY_API,
    cspApi: rawEnv.cspApi || rawEnv.CSP_API_URL || rawEnv.VITE_CSP_API_URL,
    provisioningApi: provisioningConfig.provisioningApi,
    provisioningSourceId: provisioningConfig.provisioningSourceId,
    // Third-party Services (flat structure)
    // Priority: camelCase (Docker) > UPPERCASE (Docker) > VITE_ prefix (Vite)
    sentryDsn: rawEnv.sentryDsn || rawEnv.SENTRY_DSN || rawEnv.VITE_SENTRY_DSN,
    sentryEnvironment: rawEnv.sentryEnvironment || rawEnv.SENTRY_ENVIRONMENT || rawEnv.VITE_SENTRY_ENVIRONMENT,
    pendoApiKey: rawEnv.pendoApiKey || rawEnv.PENDO_API_KEY || rawEnv.VITE_PENDO_API_KEY,
    // OIDC configuration (nested object - only nested structure)
    // Priority: structured object > clean names (Docker) > VITE_ prefix (Vite)
    oidc: rawEnv.oidc || {
        authority: rawEnv.OIDC_AUTHORITY || rawEnv.VITE_OIDC_AUTHORITY,
        clientId: rawEnv.OIDC_CLIENT_ID || rawEnv.VITE_OIDC_CLIENT_ID,
        redirectUri: rawEnv.OIDC_REDIRECT_URI || rawEnv.VITE_OIDC_REDIRECT_URI,
        postLogoutRedirect: rawEnv.OIDC_POST_LOGOUT_REDIRECT || rawEnv.VITE_OIDC_POST_LOGOUT_REDIRECT,
        silentRedirectUri: rawEnv.OIDC_SILENT_REDIRECT_URI || rawEnv.VITE_OIDC_SILENT_REDIRECT_URI,
        responseType: rawEnv.OIDC_RESPONSE_TYPE || rawEnv.VITE_OIDC_RESPONSE_TYPE,
        scope: rawEnv.OIDC_SCOPE || rawEnv.VITE_OIDC_SCOPE,
        tokenNotificationExpireTime: Number(rawEnv.OIDC_TOKEN_NOTIFICATION_EXPIRE_TIME || rawEnv.VITE_OIDC_TOKEN_NOTIFICATION_EXPIRE_TIME) || 60,
        checkSessionInterval: Number(rawEnv.OIDC_CHECK_SESSION_INTERVAL || rawEnv.VITE_OIDC_CHECK_SESSION_INTERVAL) || 2000,
        clockSkew: Number(rawEnv.OIDC_CLOCK_SKEW || rawEnv.VITE_OIDC_CLOCK_SKEW) || 300,
        silentRequestTimeout: Number(rawEnv.OIDC_SILENT_REQUEST_TIMEOUT || rawEnv.VITE_OIDC_SILENT_REQUEST_TIMEOUT) || 10000,
        log: (rawEnv.OIDC_LOG || rawEnv.VITE_LOG) === "true" || (rawEnv.OIDC_LOG || rawEnv.VITE_LOG) === true,
    },
    // Feature flags
    features: rawEnv.features || {
        pendoAnalytics: rawEnv.ENABLE_PENDO_ANALYTICS === "true" || rawEnv.ENABLE_PENDO_ANALYTICS === true,
        sentryLogging: rawEnv.ENABLE_SENTRY_LOGGING === "true" || rawEnv.ENABLE_SENTRY_LOGGING === true,
        debugMode: rawEnv.DEBUG_MODE === "true" || rawEnv.DEBUG_MODE === true,
    },
    // Build info
    build: rawEnv.build || {
        number: rawEnv.BUILD_NUMBER,
        sha: rawEnv.GIT_SHA,
        branch: rawEnv.GIT_BRANCH,
    },
    // URLs
    urls: rawEnv.urls,
};
/**
 * Helper function to safely get nested config values
 *
 * @example
 * getConfig('api.system') // Returns env.api.system
 * getConfig('oidc.authority', 'https://default.com') // With default value
 */
export function getConfig(path, defaultValue) {
    const keys = path.split(".");
    let value = env;
    for (const key of keys) {
        value = value?.[key];
        if (value === undefined) {
            return defaultValue;
        }
    }
    return value;
}
/**
 * Check if a feature flag is enabled
 *
 * @example
 * isFeatureEnabled('pendoAnalytics')
 */
export function isFeatureEnabled(feature) {
    return env.features?.[feature] === true;
}
/**
 * Check if running in development mode
 */
export function isDevelopment() {
    return env.environment === "development" || env.environment === "local" || env.debug === true;
}
/**
 * Check if running in production mode
 */
export function isProduction() {
    return env.environment === "production";
}
/**
 * Get the full API URL for an endpoint
 *
 * @example
 * getApiUrl('system', '/users') // Returns 'https://api.com/system/users'
 */
export function getApiUrl(apiName, path) {
    const baseUrl = env.api?.[apiName] || "";
    if (!path)
        return baseUrl;
    // Ensure single slash between base and path
    const cleanBase = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    return cleanBase + cleanPath;
}
// Export the env object as default
export default env;
// Log configuration source (always log for debugging)
const source = typeof window !== "undefined" && window.PSCONFIG && Object.keys(window.PSCONFIG).length > 0
    ? "Runtime (Docker)"
    : "Build-time (Vite)";
